/**
 * PR 6a — Phase 4 — ProposalKpiBlock.
 *
 * Displays 2 or 3 weighted-mean KPIs computed by `useProposalKpis`:
 *   1. aboveRev3Pct       — both roles
 *   2. aboveMinPct        — both roles (any negative = margin_at_risk → major)
 *   3. realGrossMarginPct — admin only (driven by `role` prop, NOT by value)
 *
 * Visual contract:
 *   - Each KPI is a focusable MUI Chip with a Tooltip explaining the metric.
 *   - Sign preserved with Intl.NumberFormat signDisplay: "exceptZero" — a
 *     negative aboveMinPct surfaces as "−5,2%" (margin at risk territory).
 *   - Severity colour is KIND-AWARE:
 *       • "rev3" and "margin": tri-band (≥0 ok, (−5%, 0) minor, ≤−5% major).
 *       • "min": bi-band (≥0 ok, <0 major) — per spec CA9, any unit price
 *         below min_price is `margin_at_risk`, not a "minor" warning.
 *   - prefers-reduced-motion: GAP CONHECIDO. MUI Tooltip usa Grow transition
 *     por defeito e NÃO respeita `prefers-reduced-motion: reduce` out of the
 *     box. Fix exige theme-level override (criar ThemeProvider em src/App.tsx
 *     — actualmente não existe um — com `createTheme({ components: {
 *     MuiTooltip: { defaultProps: { TransitionProps: { timeout: 0 } } } } })`
 *     guarded por media query). Decisão deferida para Phase 7 a11y audit.
 *
 * Why props are { kpis, role } and not { response }:
 *   - Keeps the component pure and trivially testable.
 *   - Caller controls re-compute timing via useProposalKpis(response).
 *   - `role` is REQUIRED to satisfy spec CA1: admin role with empty items
 *     must still render 3 chips (with "—"), not 2. We cannot infer role
 *     from `kpis.realGrossMarginPct === undefined` because that's also
 *     the empty-items state.
 *
 * Loading / empty:
 *   - `kpis.aboveRev3Pct === undefined` may mean (a) empty items, (b) denom
 *     = 0, or (c) no item carries rev3_cents. The chip shows "—" in all
 *     three. Loading is the caller's responsibility (this component is
 *     pure); the caller should pass a Skeleton wrapper if it needs to
 *     distinguish loading from empty.
 */
import React from "react";
import { Chip, Stack, Tooltip, Typography } from "@mui/material";
import type { ProposalKpis } from "../../hooks/useProposalKpis";
import type { ProposalRole } from "../../model/Proposal";

interface Props {
  kpis: ProposalKpis;
  /** JWT-derived role. Drives third-chip visibility (CA1). */
  role: ProposalRole;
  /** Forwarded for tests / debugging. */
  className?: string;
}

const PCT_FORMATTER = new Intl.NumberFormat("pt-PT", {
  style: "percent",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
  signDisplay: "exceptZero",
});

/** Minor-vs-major threshold for tri-band KPIs (rev3, margin).
 *  Aligned with the backend drift severity scale (cost/rev3 drift uses
 *  the same −5% pivot). NOT applicable to aboveMinPct, which is bi-band
 *  per spec CA9 (any negative = margin_at_risk = major). */
const MINOR_THRESHOLD = -0.05;

/** Hex tokens locked by the spec.
 *  Bypasses the MUI theme palette intentionally — these are domain
 *  semantics (drift severity), not theme decoration. */
const COLOR_MAJOR = "#B91C1C";
const COLOR_MINOR = "#B45309";
const COLOR_OK = "#15803D";
const COLOR_NEUTRAL_BG = "rgba(0,0,0,0.08)";
const COLOR_NEUTRAL_FG = "rgba(0,0,0,0.6)";

/** Severity kind selects threshold semantics.
 *   - "triBand": rev3 / margin — uses MINOR_THRESHOLD between minor & major.
 *   - "biBand":  min — any negative is major (spec CA9). */
type SeverityKind = "triBand" | "biBand";

function severityColor(
  pct: number | undefined,
  kind: SeverityKind,
): string | undefined {
  if (pct === undefined) return undefined;
  if (pct >= 0) return COLOR_OK;
  if (kind === "biBand") return COLOR_MAJOR;
  // triBand
  if (pct > MINOR_THRESHOLD) return COLOR_MINOR;
  return COLOR_MAJOR;
}

function formatPct(pct: number | undefined): string {
  if (pct === undefined) return "—";
  return PCT_FORMATTER.format(pct);
}

interface KpiChipProps {
  label: string;
  tooltip: string;
  value: number | undefined;
  kind: SeverityKind;
  testId: string;
}

const KpiChip: React.FC<KpiChipProps> = ({
  label,
  tooltip,
  value,
  kind,
  testId,
}) => {
  const semanticColor = severityColor(value, kind);
  const display = formatPct(value);
  const isNeutral = semanticColor === undefined;
  const borderColor = semanticColor ?? COLOR_NEUTRAL_BG;
  const bgColor = semanticColor ? `${semanticColor}14` : COLOR_NEUTRAL_BG;
  const fgColor = semanticColor ?? COLOR_NEUTRAL_FG;
  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Chip
        data-testid={testId}
        label={
          <Stack direction="row" spacing={1} alignItems="baseline">
            <Typography
              component="span"
              variant="caption"
              sx={{ fontWeight: 500 }}
            >
              {label}
            </Typography>
            <Typography
              component="span"
              variant="body2"
              sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
            >
              {display}
            </Typography>
          </Stack>
        }
        // Focusable for tooltip reachability by keyboard. role="status" was
        // REMOVED: it implies a live region (aria-live="polite") which would
        // cause AT to re-announce on every PricePanel re-render. The
        // `aria-label` alone carries the semantic for AT users.
        tabIndex={0}
        aria-label={`${label}: ${display}`}
        // Inline style — these are domain-semantic colours (drift severity
        // tokens locked by the spec), NOT theme decoration. Keeping them
        // inline survives palette swaps and is grep-able in QA. The 8-digit
        // hex `${color}14` encodes ~8% alpha (0x14/0xFF ≈ 0.078); supported
        // in Chromium ≥ 62 / Firefox ≥ 49 / Safari ≥ 9.1.
        style={{
          backgroundColor: bgColor,
          border: `1px solid ${borderColor}`,
          color: isNeutral ? COLOR_NEUTRAL_FG : fgColor,
        }}
        sx={{
          height: "auto",
          py: 0.5,
          px: 1,
          "& .MuiChip-label": { px: 1 },
        }}
      />
    </Tooltip>
  );
};

const ProposalKpiBlock: React.FC<Props> = ({ kpis, role, className }) => {
  // CA1: admin renders exactly 3 chips, even with empty items (3rd shows "—").
  // Comercial renders exactly 2 chips. Visibility is role-driven, NOT
  // value-driven, to disambiguate "comercial" from "admin + empty".
  const showRealMargin = role === "admin";
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ xs: "stretch", sm: "center" }}
      flexWrap="wrap"
      className={className}
      data-testid="proposal-kpi-block"
      role="group"
      aria-label="Indicadores de margem"
    >
      <KpiChip
        label="Acima REV3"
        tooltip="Diferença média ponderada entre preço unitário e REV3, por quantidade. Negativo = abaixo da referência comercial."
        value={kpis.aboveRev3Pct}
        kind="triBand"
        testId="kpi-above-rev3"
      />
      <KpiChip
        label="Acima mínimo"
        tooltip="Diferença média ponderada entre preço unitário e preço mínimo permitido. Qualquer negativo = margem em risco."
        value={kpis.aboveMinPct}
        kind="biBand"
        testId="kpi-above-min"
      />
      {showRealMargin && (
        <KpiChip
          label="Margem real"
          tooltip="Margem bruta real (sobre preço de venda). Visível apenas a admin."
          value={kpis.realGrossMarginPct}
          kind="triBand"
          testId="kpi-real-margin"
        />
      )}
    </Stack>
  );
};

export default ProposalKpiBlock;
